<template>
  <form autocomplete="off" class="v-form" @submit.prevent="cancelSubmit()">
    <slot />

    <VFormBlueprint v-if="blueprint" :blueprint @updated="emit('questionUpdated', $event)">
      <template #sidebar>
        <slot name="sidebar" />
      </template>
      <template #question-header-bottom="slotProps">
        <slot name="question-header-bottom" v-bind="slotProps" />
      </template>
      <template #answer="slotProps">
        <slot name="answer" v-bind="slotProps" />
      </template>
      <template #after-question-answer="slotProps">
        <slot name="after-question-answer" v-bind="slotProps" />
      </template>
      <template #file-name="slotProps">
        <slot name="file-name" v-bind="slotProps" />
      </template>
      <template #dropdown-option="slotProps">
        <slot name="dropdown-option" v-bind="slotProps" />
      </template>
      <template #true-false-option="slotProps">
        <slot name="true-false-option" v-bind="slotProps" />
      </template>
    </VFormBlueprint>
  </form>
</template>

<script setup lang="ts">
import { debounce as _debounce } from 'lodash-es'
import { inject, provide, watch } from 'vue'

import { type FormErrors, type FormRule, useForm } from '../composables/useForm.ts'
import type { FileLike, VFormTrigger } from '../types.ts'
import { configVForm as config } from '../utils/config.ts'
import { $dialogKey, $formKey } from '../utils/injections.ts'
import { type Blueprint, type DropdownQuestion, type Question, type QuestionOption, type TrueFalseQuestion } from './FormBuilder/useBlueprint.ts'
import VFormBlueprint from './VFormBlueprint.vue'

type Form = Record<string, any> | ReturnType<typeof useForm>

export interface Props {
  form?: Form
  rules?: Record<string, FormRule | FormRule[]>
  errors?: FormErrors
  validateOn?: VFormTrigger
  debounce?: number
  disabled?: boolean
  blueprint?: Blueprint
  resetOnDialogClose?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  form: () => ({}),
  rules: () => ({}),
  errors: () => ({}),
  disabled: false,
  validateOn: undefined,
  debounce: undefined,
  blueprint: undefined,
  resetOnDialogClose: undefined,
})

defineSlots<{
  default(): any
  sidebar(): any
  answer(props: { question: Question }): any
  'after-question-answer'(props: { question: Question }): any
  'file-name'(props: { file: FileLike; index: number; question: Question }): any
  'question-header-bottom'(props: { question: Question }): any
  'true-false-option'(props: { question: TrueFalseQuestion; option: QuestionOption }): any
  'dropdown-option'(props: { question: DropdownQuestion; option: QuestionOption }): any
}>()

const emit = defineEmits<{
  submit: [value: Form]
  'update:errors': [value: FormErrors]
  blur: [prop: string]
  questionUpdated: [question: Question]
}>()

const dialog = inject($dialogKey, undefined)

const form = (props.form.$rules ? props.form : useForm(props.form)) as ReturnType<typeof useForm>

// When the form prop is not a useForm return value, we need to assign our own props to it.
// Also need to watch for changes to the form prop, and update the `useForm` form.
if (!props.form.$rules) {
  form.$validateOn = props.validateOn

  watch(() => props.rules, form.replaceRules, { immediate: true, deep: true })

  watch(
    () => props.form,
    (data) => Object.assign(form, data),
    { deep: true },
  )
}

watch(
  () => form.$errors,
  () => emit('update:errors', form.$errors),
  { deep: true },
)

watch(
  () => props.errors,
  (errors) => (form.$errors = errors),
  { deep: true },
)

async function originalSubmit() {
  if (props.form.$rules) {
    await form.submit()
  } else {
    await form.validate()
  }

  emit('submit', props.form)
}

let submit = _debounce(originalSubmit, 0)
watch(() => [props.debounce, config.debounce], (submit = _debounce(originalSubmit, props.debounce ?? config.debounce)))

function cancelSubmit() {
  return false
}

const timeouts: Record<string, number> = {}

function debounceFn(fn: (prop: string) => void, prop: string, delay: number) {
  clearTimeout(timeouts[prop])
  timeouts[prop] = window.setTimeout(() => fn(prop), delay)
}

function validateOneProp(prop: string, trigger?: VFormTrigger) {
  if (dialog && !dialog.visible.value) {
    return
  }

  if (!trigger || trigger === (form.$validateOn ?? config.validateOn)) {
    if (trigger === 'keyup') {
      debounceFn(form._validateProp, prop, form.$debounce ?? config.debounce)
    } else {
      form._validateProp(prop)

      if (trigger === 'blur') {
        emit('blur', prop)
      }
    }
  }
}

watch(
  () => dialog?.visible.value,
  (visible) => {
    if (!visible && (props.resetOnDialogClose ?? config.resetOnDialogClose)) {
      form.reset()
      form.$errors = {}
    }
  },
)

provide($formKey, {
  props,
  form,
  submit,
  validateOneProp,
})

defineExpose({
  originalSubmit,
  submit,
  validate: form.validate,
  validateProp: form._validateProp,
})
</script>

<style>
.v-form {
  position: relative;

  > .v-input,
  > .v-select,
  > .v-datepicker,
  > .v-switch,
  > .v-checkbox-group,
  > .v-radio-group,
  > .v-transition-collapse > .v-input,
  > .v-transition-collapse > .v-select,
  > .v-transition-collapse > .v-datepicker,
  > .v-transition-collapse > .v-switch,
  > .v-transition-collapse > .v-checkbox-group,
  > .v-transition-collapse > .v-radio-group {
    margin-bottom: 15px;
  }
}
</style>
